import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../api';

const STATUS_CONFIG = {
  new:        { label: 'New',        bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
  contacted:  { label: 'Contacted',  bg: '#fefce8', text: '#a16207', border: '#fde68a' },
  interested: { label: 'Interested', bg: '#f0fdf4', text: '#15803d', border: '#bbf7d0' },
  converted:  { label: 'Converted',  bg: '#f0fdfa', text: '#0d9488', border: '#99f6e4' },
  dead:       { label: 'Dead',       bg: '#f8fafc', text: '#94a3b8', border: '#e2e8f0' },
};

const METHOD_ICONS = { call: '📞', email: '✉️', mail: '📬', text: '💬', note: '📝' };

const TABS = ['all', 'new', 'contacted', 'interested', 'converted', 'dead'];

const INPUT = { width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 7,
  border: '1px solid #e2e8f0', background: '#f8fafc', color: '#1e293b', boxSizing: 'border-box' };

// Hawaii County assessment roll download URL
const HAWAII_COUNTY_URL = 'https://www.hawaiipropertytax.com/reports.html';

// ── CSV parser ────────────────────────────────────────────────────────────────
function parseCSV(text) {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, '').toLowerCase().replace(/\s+/g, '_'));

  function mapHeader(h) {
    if (h.includes('parcel') || h.includes('tmk')) return 'parcel_id';
    if (h.includes('owner')) return 'owner_name';
    if (h.includes('property_addr') || h.includes('situs')) return 'property_address';
    if (h.includes('mail') && h.includes('addr')) return 'mailing_address';
    if (h.includes('mail') && h.includes('city')) return 'mailing_city';
    if (h.includes('mail') && h.includes('state')) return 'mailing_state';
    if (h.includes('mail') && h.includes('zip')) return 'mailing_zip';
    if (h.includes('assessed') || h.includes('total_value') || h.includes('value')) return 'assessed_value';
    if (h.includes('land_use') || h.includes('use_code') || h.includes('class')) return 'land_use';
    return h;
  }

  const mapped = headers.map(mapHeader);

  return lines.slice(1).map(line => {
    const cols = line.match(/(".*?"|[^,]+|(?<=,)(?=,)|(?<=,)$|^(?=,))/g) || [];
    const obj = {};
    mapped.forEach((k, i) => {
      obj[k] = (cols[i] || '').trim().replace(/^"|"$/g, '');
    });
    return obj;
  }).filter(r => r.parcel_id);
}

function filterOutOfState(leads) {
  return leads.filter(l => l.mailing_state && l.mailing_state.toUpperCase() !== 'HI');
}

// ── Drag-drop zone ────────────────────────────────────────────────────────────
function DropZone({ onFile }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef();

  const handleDrop = useCallback(e => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) onFile(file);
  }, [onFile]);

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current.click()}
      style={{
        border: `2px dashed ${dragging ? '#0d9488' : '#cbd5e1'}`,
        borderRadius: 12, padding: '32px 24px', textAlign: 'center', cursor: 'pointer',
        background: dragging ? '#f0fdfa' : '#f8fafc',
        transition: 'all .15s',
      }}>
      <input ref={inputRef} type="file" accept=".csv" style={{ display: 'none' }}
        onChange={e => e.target.files[0] && onFile(e.target.files[0])} />
      <div style={{ fontSize: 28, marginBottom: 8 }}>📂</div>
      <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 14 }}>
        {dragging ? 'Drop it!' : 'Drag & drop your CSV here'}
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>or click to browse</div>
      <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
        <span style={{ fontSize: 12, color: '#64748b' }}>Need the data? </span>
        <a href={HAWAII_COUNTY_URL} target="_blank" rel="noreferrer"
          style={{ fontSize: 12, color: '#0d9488', fontWeight: 600 }}
          onClick={e => e.stopPropagation()}>
          Download Hawaii County Assessment Roll ↗
        </a>
      </div>
    </div>
  );
}

// ── Import preview modal ──────────────────────────────────────────────────────
function ImportModal({ file, onDone, onClose }) {
  const [step, setStep] = useState('parsing'); // parsing | preview | importing | done
  const [allRows, setAllRows] = useState([]);
  const [outOfState, setOutOfState] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const reader = new FileReader();
    reader.onload = e => {
      const rows = parseCSV(e.target.result);
      const filtered = filterOutOfState(rows);
      setAllRows(rows);
      setOutOfState(filtered);
      setStep('preview');
    };
    reader.readAsText(file);
  }, [file]);

  async function doImport() {
    setStep('importing');
    try {
      const { data } = await api.post('/pm/leads/import', { leads: outOfState });
      setResult(data);
      setStep('done');
      onDone();
    } catch (err) {
      setError(err?.response?.data?.error || 'Import failed');
      setStep('preview');
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 520, padding: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b', marginBottom: 16 }}>Import CSV</div>

        {step === 'parsing' && (
          <div style={{ textAlign: 'center', padding: 32, color: '#64748b' }}>Parsing {file.name}…</div>
        )}

        {step === 'preview' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: 14, textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>{allRows.length.toLocaleString()}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Total rows</div>
              </div>
              <div style={{ background: '#f0fdf4', borderRadius: 8, padding: 14, textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#15803d' }}>{outOfState.length.toLocaleString()}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Out-of-state owners</div>
              </div>
            </div>
            {outOfState.length > 0 && (
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: 12, marginBottom: 16,
                maxHeight: 200, overflowY: 'auto', fontSize: 12, color: '#475569' }}>
                <div style={{ fontWeight: 600, marginBottom: 8, color: '#1e293b' }}>Preview (first 5):</div>
                {outOfState.slice(0, 5).map((l, i) => (
                  <div key={i} style={{ marginBottom: 4, paddingBottom: 4,
                    borderBottom: i < 4 ? '1px solid #e2e8f0' : 'none' }}>
                    <strong>{l.owner_name || '—'}</strong> · {l.property_address || '—'} · {l.mailing_state}
                  </div>
                ))}
              </div>
            )}
            {outOfState.length === 0 && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
                padding: 12, fontSize: 13, color: '#dc2626', marginBottom: 16 }}>
                No out-of-state owners found. Make sure your CSV has a mailing state column.
              </div>
            )}
            {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={doImport}
                disabled={outOfState.length === 0}>
                Import {outOfState.length.toLocaleString()} Leads
              </button>
              <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            </div>
          </>
        )}

        {step === 'importing' && (
          <div style={{ textAlign: 'center', padding: 32, color: '#64748b' }}>
            Importing {outOfState.length.toLocaleString()} leads…
          </div>
        )}

        {step === 'done' && result && (
          <>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8,
              padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#15803d', marginBottom: 8 }}>✓ Import complete</div>
              <div style={{ fontSize: 13, color: '#475569' }}>
                <div>Added: <strong>{result.inserted}</strong></div>
                <div>Skipped (duplicates): <strong>{result.skipped}</strong></div>
              </div>
            </div>
            <button className="btn btn-primary btn-sm" onClick={onClose}>Done</button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Comms panel ───────────────────────────────────────────────────────────────
function CommsPanel({ lead, onClose, onStatusChange }) {
  const [comms, setComms] = useState([]);
  const [loadingComms, setLoadingComms] = useState(true);
  const [method, setMethod] = useState('call');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [newStatus, setNewStatus] = useState(lead.status);

  function loadComms() {
    api.get(`/pm/leads/${lead.id}/comms`).then(r => setComms(r.data)).catch(() => {}).finally(() => setLoadingComms(false));
  }
  useEffect(() => { loadComms(); }, [lead.id]);

  async function logComm() {
    if (!notes.trim()) return;
    setSaving(true);
    try {
      await api.post(`/pm/leads/${lead.id}/comms`, { method, notes });
      setNotes('');
      loadComms();
    } catch {} finally { setSaving(false); }
  }

  async function updateStatus(s) {
    setNewStatus(s);
    await api.put(`/pm/leads/${lead.id}`, { status: s }).catch(() => {});
    onStatusChange();
  }

  const sc = STATUS_CONFIG[newStatus] || STATUS_CONFIG.new;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
      alignItems: 'flex-start', justifyContent: 'flex-end', zIndex: 1000 }}>
      <div className="card" style={{ width: 420, height: '100vh', borderRadius: '12px 0 0 12px',
        overflowY: 'auto', padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>{lead.owner_name || 'Unknown Owner'}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{lead.property_address}</div>
            {lead.mailing_state && (
              <div style={{ fontSize: 12, color: '#94a3b8' }}>
                Mails to: {lead.mailing_city}, {lead.mailing_state} {lead.mailing_zip}
              </div>
            )}
            {lead.assessed_value && (
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                Assessed: ${Number(lead.assessed_value).toLocaleString()}
              </div>
            )}
          </div>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#94a3b8' }}>✕</button>
        </div>

        {/* Status */}
        <div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6, fontWeight: 600 }}>Status</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {Object.entries(STATUS_CONFIG).map(([s, c]) => (
              <button key={s} onClick={() => updateStatus(s)}
                style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20,
                  border: `1px solid ${newStatus === s ? c.border : '#e2e8f0'}`,
                  background: newStatus === s ? c.bg : '#f8fafc',
                  color: newStatus === s ? c.text : '#64748b', cursor: 'pointer' }}>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Log contact */}
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', marginBottom: 10 }}>Log Contact</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {Object.entries(METHOD_ICONS).map(([m, icon]) => (
              <button key={m} onClick={() => setMethod(m)}
                style={{ fontSize: 11, padding: '4px 10px', borderRadius: 16,
                  border: `1px solid ${method === m ? '#0d9488' : '#e2e8f0'}`,
                  background: method === m ? '#f0fdfa' : '#f8fafc',
                  color: method === m ? '#0d9488' : '#64748b', cursor: 'pointer' }}>
                {icon} {m}
              </button>
            ))}
          </div>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
            placeholder="Notes about this contact…"
            style={{ ...INPUT, resize: 'vertical', marginBottom: 10 }} />
          <button className="btn btn-primary btn-sm" onClick={logComm} disabled={saving || !notes.trim()}>
            {saving ? 'Saving…' : 'Log'}
          </button>
        </div>

        {/* History */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', marginBottom: 10 }}>History</div>
          {loadingComms ? <div style={{ color: '#94a3b8', fontSize: 13 }}>Loading…</div>
            : comms.length === 0 ? <div style={{ color: '#94a3b8', fontSize: 13 }}>No contacts logged yet.</div>
            : comms.map(c => (
              <div key={c.id} style={{ paddingBottom: 10, marginBottom: 10,
                borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 14 }}>{METHOD_ICONS[c.method] || '📝'}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#475569' }}>{c.method}</span>
                  <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto' }}>
                    {new Date(c.created_at).toLocaleDateString()}
                  </span>
                </div>
                {c.notes && <div style={{ fontSize: 12, color: '#475569' }}>{c.notes}</div>}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

// ── Main view ─────────────────────────────────────────────────────────────────
export default function LeadsView() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('new');
  const [search, setSearch] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [selected, setSelected] = useState(null);

  function load() {
    const params = {};
    if (tab !== 'all') params.status = tab;
    if (search) params.search = search;
    api.get('/pm/leads', { params }).then(r => setLeads(r.data)).catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => { setLoading(true); load(); }, [tab, search]);

  const counts = {};
  // We'll rely on server-side counts via separate load if needed — for now show filtered count

  return (
    <>
      <div className="page-header">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span className="page-title">Lead Pipeline</span>
          <span style={{ fontSize: 12, color: '#64748b' }}>Out-of-state property owners — Hawaii County</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <a href={HAWAII_COUNTY_URL} target="_blank" rel="noreferrer"
            className="btn btn-ghost btn-sm" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            📥 Download Assessment Roll
          </a>
        </div>
      </div>

      <div className="page-body">
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 2, marginBottom: 16, flexWrap: 'wrap' }}>
          {TABS.map(t => {
            const sc = STATUS_CONFIG[t] || { label: 'All', bg: '#f8fafc', text: '#475569' };
            const active = tab === t;
            return (
              <button key={t} onClick={() => setTab(t)}
                style={{ fontSize: 12, fontWeight: active ? 700 : 400, padding: '6px 14px',
                  borderRadius: 20, border: `1px solid ${active ? (sc.border || '#cbd5e1') : '#e2e8f0'}`,
                  background: active ? (sc.bg || '#f1f5f9') : '#fff',
                  color: active ? (sc.text || '#475569') : '#64748b', cursor: 'pointer' }}>
                {t === 'all' ? 'All' : sc.label}
              </button>
            );
          })}
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, address…"
            style={{ ...INPUT, width: 220, marginLeft: 'auto' }} />
        </div>

        {/* Drop zone shown when no leads */}
        {!loading && leads.length === 0 && tab === 'new' && !search ? (
          <DropZone onFile={setImportFile} />
        ) : (
          <>
            {/* Import strip */}
            <div style={{ marginBottom: 14 }}>
              <DropZone onFile={setImportFile} />
            </div>

            {loading ? <div className="loading">Loading…</div> : (
              leads.length === 0 ? (
                <div className="card">
                  <div className="card-body" style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                    No leads in this stage.
                  </div>
                </div>
              ) : (
                <div className="card">
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Owner</th>
                          <th>Property</th>
                          <th>Mails To</th>
                          <th>Assessed</th>
                          <th>Land Use</th>
                          <th>Status</th>
                          <th>Contacts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leads.map(l => {
                          const sc = STATUS_CONFIG[l.status] || STATUS_CONFIG.new;
                          return (
                            <tr key={l.id} onClick={() => setSelected(l)}
                              style={{ cursor: 'pointer' }}>
                              <td style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>
                                {l.owner_name || '—'}
                              </td>
                              <td style={{ fontSize: 12, color: '#475569' }}>{l.property_address || '—'}</td>
                              <td style={{ fontSize: 12, color: '#64748b' }}>
                                {l.mailing_city ? `${l.mailing_city}, ` : ''}{l.mailing_state}
                              </td>
                              <td style={{ fontSize: 12, color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
                                {l.assessed_value ? `$${Number(l.assessed_value).toLocaleString()}` : '—'}
                              </td>
                              <td style={{ fontSize: 11, color: '#94a3b8' }}>{l.land_use || '—'}</td>
                              <td>
                                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px',
                                  borderRadius: 12, background: sc.bg, color: sc.text,
                                  border: `1px solid ${sc.border}` }}>
                                  {sc.label}
                                </span>
                              </td>
                              <td style={{ fontSize: 12, color: l.comm_count > 0 ? '#0d9488' : '#94a3b8' }}>
                                {l.comm_count > 0 ? `${l.comm_count} log${l.comm_count > 1 ? 's' : ''}` : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            )}
          </>
        )}
      </div>

      {importFile && (
        <ImportModal file={importFile} onDone={() => { load(); }} onClose={() => setImportFile(null)} />
      )}
      {selected && (
        <CommsPanel lead={selected} onClose={() => setSelected(null)} onStatusChange={load} />
      )}
    </>
  );
}
